import { useEffect, useMemo, useState } from "react";
import { api, day } from "../api.js";
import { useResource } from "./useResource.js";
import { ENTITY, SYSTEM, VAT, GROUPS, FIELD, describe } from "../../../lib/taxpayer/profile.js";
import { SECTIONS } from "./Company.jsx";

// Податковий профіль компанії (docs/taxpayer-profiles-plan.md, TP-04): хто платник, система, група ЄП, ПДВ — з датою.
// Від нього залежать розділи меню, синхронізація з ДПС і звітність. Відомості з картки ДПС — лише пропозиція з джерелом
// кожного поля; підтверджує власник. Зміна — новим записом з дати, попередні лишаються в історії
const title = (k) => SECTIONS.find((s) => s.key === k)?.title || k;
const blank = { entity_kind: "", tax_system: "", single_tax_group: "", vat_status: "", valid_from: "" };
const pick = (p) => (p ? { entity_kind: p.entity_kind || "", tax_system: p.tax_system || "", single_tax_group: p.single_tax_group ?? "", vat_status: p.vat_status || "", valid_from: p.valid_from || "" } : blank);

// Поле з текстом помилки — поза компонентом, щоб React не перестворював його (і не губив фокус) на кожну зміну
function Field({ f, label, error, children }) {
  return <label className="field">{label}{children}{error && <span className="pn-err" id={`tp-${f}-err`}>{error}</span>}</label>;
}

export default function TaxProfile({ companyId, onChange }) {
  const url = `/api/companies/${companyId}/tax-profile`;
  const [rev, setRev] = useState(0);
  const r = useResource(url, rev);
  const d = r.data;
  const [form, setForm] = useState(null), [reason, setReason] = useState("");
  const [pv, setPv] = useState(null), [busy, setBusy] = useState(false), [err, setErr] = useState(null), [saved, setSaved] = useState(false);
  const editing = form !== null;
  const start = () => { setForm(pick(d.current ? { ...d.current, valid_from: "" } : d.candidate)); setReason(""); setErr(null); setSaved(false); };
  // Наслідки — з сервера (ті самі правила, що застосовуються): що приховається, які звіти, що ще не підтримується
  useEffect(() => {
    if (!form) return;
    const t = setTimeout(() => api(`${url}/preview`, { method: "POST", body: { profile: form } }).then(setPv, () => {}), 250);
    return () => clearTimeout(t);
  }, [form, url]);
  const errOf = (f) => pv?.errors?.find((x) => x.field === f)?.message;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "tax_system" && v !== "single" ? { single_tax_group: "" } : {}) }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api(url, { method: "POST", body: { profile: form, expectedRevision: d.revision, reason: reason || undefined } }); setForm(null); setSaved(true); setRev((x) => x + 1); onChange?.(); }
    catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  if (r.pending) return <section className="card"><p className="muted" role="status">Податковий профіль…</p></section>;
  if (r.error) return <section className="card"><p className="error">{r.error.message}</p></section>;
  const cur = d.current, cand = d.candidate;
  const hidden = pv?.capabilities ? Object.entries(pv.capabilities.sections).filter(([, s]) => !s.applicable).map(([k]) => title(k)) : [];
  const sel = (f, options, label) => (
    <Field f={f} label={label} error={errOf(f)}>
      <select value={form[f]} onChange={(e) => set(f, e.target.value)} aria-invalid={errOf(f) ? true : undefined} aria-describedby={errOf(f) ? `tp-${f}-err` : undefined}>
        <option value="">Оберіть</option>{options}
      </select>
    </Field>
  );
  return (
    <section className="card tax-profile" aria-labelledby="tp-h">
      <h3 id="tp-h" className="block-title">Податковий профіль</h3>
      <p className="muted small">Від профілю залежить, які розділи, синхронізації з ДПС і звітність показує eCabinet. Режим не потрібно обирати в кожному майстрі.</p>
      {saved && <p className="pn-ok" role="status">✓ Профіль підтверджено — меню й звітність оновлено.</p>}
      {cur ? (
        <div className="tp-current">
          <p><b>{cur.text}</b> · діє з {day(cur.valid_from)}</p>
          <ul className="small tp-evidence">{Object.keys(FIELD).filter((k) => cur.evidence?.[k]).map((k) => <li key={k}><b>{FIELD[k]}:</b> {cur.evidence[k].source === "payer_card" ? `за карткою ДПС — ${cur.evidence[k].label}` : "вказано вручну"}</li>)}</ul>
          {d.conflicts?.length > 0 && (
            <div className="card warn small" role="alert"><b>Картка ДПС показує інше:</b>
              <ul className="tp-evidence">{d.conflicts.map((x) => <li key={x.field}>{FIELD[x.field]}: {x.card}</li>)}</ul>
              Якщо картка застаріла — оновіть «Дані компанії»; якщо змінився ваш статус — змініть профіль з дати зміни.</div>
          )}
          <p className="muted small">Підтвердив {cur.confirmedBy || "—"} {cur.confirmedAt ? new Date(cur.confirmedAt).toLocaleString("uk-UA") : ""}</p>
        </div>
      ) : (
        <div className="card warn tp-unconfirmed" role="status">
          <b>Податковий профіль ще не підтверджено.</b> Поки що eCabinet працює як для ТОВ — платника ПДВ на загальній системі.
          {cand ? <> За карткою платника ДПС{d.cardFetchedAt ? ` (від ${new Date(d.cardFetchedAt).toLocaleDateString("uk-UA")})` : ""}: <b>{describe(cand)}</b>{cand.valid_from ? `, з ${day(cand.valid_from)}` : ""}.
            <ul className="small tp-evidence">{Object.values(cand.evidence || {}).map((e, i) => <li key={i}>{e.label}</li>)}</ul></>
            : <> Картку платника ще не завантажено — синхронізуйте «Дані компанії» або заповніть профіль вручну.</>}
        </div>
      )}
      {d.canEdit && !editing && <div className="actions"><button type="button" className="btn btn-sm" onClick={start}>{cur ? "Змінити з дати…" : cand ? "Перевірити й підтвердити" : "Заповнити профіль"}</button></div>}
      {!d.canEdit && <p className="muted small">Підтверджує й змінює профіль власник компанії.</p>}
      {editing && (
        <form className="tp-form" onSubmit={save}>
          <div className="tp-grid">
            {sel("entity_kind", Object.entries(ENTITY).map(([k, v]) => <option key={k} value={k}>{v}</option>), "Хто платник")}
            {sel("tax_system", Object.entries(SYSTEM).map(([k, v]) => <option key={k} value={k}>{v}</option>), "Система оподаткування")}
            {form.tax_system === "single" && sel("single_tax_group", GROUPS.map((g) => <option key={g} value={g}>{g} група</option>), "Група єдиного податку")}
            {sel("vat_status", Object.entries(VAT).map(([k, v]) => <option key={k} value={k}>{v}</option>), "ПДВ")}
            <Field f="valid_from" label={cur ? "Діє з (дата зміни)" : "Діє з"} error={errOf("valid_from")}>
              <input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} aria-invalid={errOf("valid_from") ? true : undefined} />
            </Field>
            <label className="field">Причина / примітка<input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder={cur ? "Наприклад: анульовано реєстрацію ПДВ" : "Необов'язково"} /></label>
          </div>
          {pv?.capabilities && (
            <div className="tp-preview small" aria-live="polite">
              <p><b>{pv.text}</b></p>
              <p>Звітність: декларація з ПДВ — {pv.capabilities.reports.vat ? "так" : "ні"}; фінзвітність — {pv.capabilities.reports.fin ? "так" : "ні"}; ПДФО / ВЗ / ЄСВ — так.</p>
              {hidden.length > 0 && <p>Не стосується й буде приховано: {hidden.join(", ")}.</p>}
              {pv.capabilities.notYet.length > 0 && <p className="notice">eCabinet ще не вміє: {pv.capabilities.notYet.join("; ")}. Ці розділи з'являться пізніше.</p>}
            </div>
          )}
          {cur && <p className="muted small">Попередній профіль діятиме до цієї дати й лишиться в історії; документи, вже складені за ним, не змінюються.</p>}
          {err && <p className="error small" role="alert">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn btn-sm" disabled={busy || !pv || pv.errors?.length > 0}>{busy ? "Зберігаємо…" : "Підтвердити профіль"}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setForm(null); setErr(null); }}>Скасувати</button>
          </div>
        </form>
      )}
      {d.history.length > 1 && (
        <details className="small tp-history"><summary>Історія профілю ({d.history.length})</summary>
          <ul>{d.history.map((h) => <li key={h.id} className={h.supersededAt ? "muted" : ""}>{day(h.valid_from)}{h.valid_to ? ` — ${day(h.valid_to)}` : " — дотепер"}: {h.text}{h.supersededAt ? " · замінено" : ""}{h.reason ? ` · ${h.reason}` : ""}</li>)}</ul>
        </details>
      )}
    </section>
  );
}
